import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'
import { defineStore, storeToRefs } from 'pinia'

import EventBus, { GlobalEvents, VideoCallEvents } from '@shared/event-bus'
import { Namespaces } from '@shared/models/enums/namespaces'
import { AttachmentContentTypes } from '@shared/models/enums/quick-forms'
import { DataMessageTypes } from '@shared/models/interfaces/quick-messages'
import { IMessage } from '@shared/models/proto/base/messages'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { useVideoCallCompatibilityStore } from '@shared/store/video-call-compatibility'
import {
  room,
  useVideoCallConfigsStore,
} from '@shared/store/video-call-configs'
import { lsGet, LsGlobalKeys, lsHas } from '@shared/utils/helpers/local-storage'
import { rtcSessionValid } from '@shared/utils/helpers/video-call'
import { VideoCallSession } from '@shared/utils/models/video-call-config'

import {
  EndScreenSharingCallDTO,
  EndVideoCallDTO,
  InitiateScreensharingCallDTO,
  InitiateVideoCallDTO,
  InviteTranslatorDTO,
  InviteVideoCallDTO,
  ITranslatorInviteData,
  IVideoCallInviteData,
} from '@vc/models/proto/dto/video-call'
import { TwilioSessionRO } from '@vc/models/proto/ro/twilio'
import { useConversationsStore } from '@vc/store/conversations'
import { useRtcSessionsStore } from '@vc/store/rtc-sessions'

export interface State {
  isPatientVideoTestFailed: boolean
}

const state = (): State => ({
  isPatientVideoTestFailed: false,
})

export const apiTransport = new ApiTransport(resourcePath, 'vc')

export const useVideoCallStore = defineStore('video-chat', {
  state,
  actions: {
    inviteChatInterpretor(payload: { data: ITranslatorInviteData }) {
      return apiTransport.post<InviteTranslatorDTO>(
        '/video_calls/invite_translator',
        new InviteTranslatorDTO(payload),
        InviteTranslatorDTO
      )
    },

    async restoreVideoCallFromCache() {
      const isCachedVideoCall = lsHas(LsGlobalKeys.CurrentVideoCall)
      if (!isCachedVideoCall) {
        return
      }

      const rtcSessionsStore = useRtcSessionsStore()
      const videoCallConfigsStore = useVideoCallConfigsStore()

      const videoCallSession: VideoCallSession = JSON.parse(
        lsGet(LsGlobalKeys.CurrentVideoCall)
      )

      const session = await rtcSessionsStore.fetchRtcSession({
        roomId: videoCallSession.roomId ?? '',
      })

      if (!rtcSessionValid(session.data)) {
        videoCallConfigsStore.cleanCachedVideoConversation()
        return
      }

      await this.start()
    },

    async start() {
      const requestedAt = new Date().toISOString()
      const videoCallConfigsStore = useVideoCallConfigsStore()

      const isCachedVideoCall = lsHas(LsGlobalKeys.CurrentVideoCall)

      if (isCachedVideoCall) {
        const videoCallSession: VideoCallSession = JSON.parse(
          lsGet(LsGlobalKeys.CurrentVideoCall)
        )

        if (videoCallSession?.isScreenSharingMode) {
          this.initiateScreenSharing()
          return
        }
      }

      videoCallConfigsStore.isLoading = true
      videoCallConfigsStore.isVideoChatMode = true

      const videoCallCompatibilityStore = useVideoCallCompatibilityStore()
      const { reasonForFailure } = storeToRefs(videoCallCompatibilityStore)

      await videoCallCompatibilityStore.ensureCompatabilityIsFetched(
        Namespaces.VC
      )

      if (reasonForFailure.value) {
        videoCallConfigsStore.compatibilityError = reasonForFailure.value
        videoCallConfigsStore.isLoading = false
        return
      }

      try {
        if (!isCachedVideoCall) {
          const payload = {
            data: {
              requestedAt,
              conversationId: videoCallConfigsStore.conversation?.id ?? 0,
            },
          }

          const { data } = (await apiTransport.post(
            '/video_calls/initiate_video_call',
            new InitiateVideoCallDTO(payload),
            InitiateVideoCallDTO,
            TwilioSessionRO
          )) ?? { data: {} }

          if (data) {
            videoCallConfigsStore.$patch(data)
          }
        }

        await videoCallConfigsStore.initializeTracks()
        if (
          videoCallConfigsStore.mediaError ||
          videoCallConfigsStore.compatibilityError
        ) {
          return false
        }
        videoCallConfigsStore.applyRoomEventListeners({
          namespace: Namespaces.VC,
        })
        videoCallConfigsStore.initializeParticipants()
        return !isCachedVideoCall
      } catch (e) {
        videoCallConfigsStore.cleanCachedVideoConversation()

        videoCallConfigsStore.isVideoChatMode = false
        videoCallConfigsStore.disconnectFromRoom()
        /* eslint-disable-next-line no-console */
        console.log('Error during starting a video call', e)
      } finally {
        videoCallConfigsStore.isLoading = false
      }
    },

    async initiateScreenSharing() {
      const requestedAt = new Date().toISOString()
      const videoCallConfigsStore = useVideoCallConfigsStore()

      const isCachedVideoCall = lsHas(LsGlobalKeys.CurrentVideoCall)

      videoCallConfigsStore.isLoading = true
      videoCallConfigsStore.isVideoChatMode = true
      videoCallConfigsStore.setScreenSharingMode(true)

      const videoCallCompatibilityStore = useVideoCallCompatibilityStore()
      const { reasonForFailure } = storeToRefs(videoCallCompatibilityStore)

      await videoCallCompatibilityStore.ensureCompatabilityIsFetched(
        Namespaces.VC
      )

      if (reasonForFailure.value) {
        videoCallConfigsStore.compatibilityError = reasonForFailure.value
        videoCallConfigsStore.isLoading = false
        return
      }

      try {
        if (!isCachedVideoCall) {
          const payload = {
            data: {
              requestedAt,
              conversationId: videoCallConfigsStore.conversation?.id ?? 0,
            },
          }

          const { data } = (await apiTransport.post(
            '/screensharing_calls/initiate_screensharing_call',
            new InitiateScreensharingCallDTO(payload),
            InitiateScreensharingCallDTO,
            TwilioSessionRO
          )) ?? { data: {} }

          if (data) {
            videoCallConfigsStore.$patch(data)
          }
        }

        await videoCallConfigsStore.initializeTracks()
        if (
          videoCallConfigsStore.mediaError ||
          videoCallConfigsStore.compatibilityError
        ) {
          return false
        }
        videoCallConfigsStore.applyRoomEventListeners({
          namespace: Namespaces.VC,
        })
        videoCallConfigsStore.initializeParticipants()
        return !isCachedVideoCall
      } catch (e) {
        videoCallConfigsStore.cleanCachedVideoConversation()

        videoCallConfigsStore.isVideoChatMode = false
        videoCallConfigsStore.setScreenSharingMode(false)

        videoCallConfigsStore.disconnectFromRoom()
        /* eslint-disable-next-line no-console */
        console.log('Error during starting a video call', e)
      } finally {
        videoCallConfigsStore.isLoading = false
      }
    },

    async endVideoCall(data: {
      roomId: string
      epochTime: number
      createdAt: string
    }) {
      await apiTransport.post(
        '/video_calls/end_video_call',
        new EndVideoCallDTO({ data }),
        EndVideoCallDTO,
        undefined,
        {
          onUploadProgress({ total, loaded }) {
            EventBus.emit(GlobalEvents.AsyncRequestProgress, {
              total,
              loaded,
            })
          },
        }
      )
    },

    async endScreenSharing(data: {
      roomId: string
      epochTime: number
      createdAt: string
    }) {
      await apiTransport.post(
        '/screensharing_calls/end_screensharing_call',
        new EndScreenSharingCallDTO({ data }),
        EndScreenSharingCallDTO,
        undefined,
        {
          onUploadProgress({ total, loaded }) {
            EventBus.emit(GlobalEvents.AsyncRequestProgress, {
              total,
              loaded,
            })
          },
        }
      )
    },

    async onHangUp() {
      const videoCallConfigsStore = useVideoCallConfigsStore()
      if (room) {
        if (!room.participants.size) {
          if (videoCallConfigsStore.isScreenSharingMode) {
            await this.endScreenSharing({
              roomId: room.sid,
              epochTime: dayjs().unix(),
              createdAt: dayjs().toISOString(),
            })
          } else {
            await this.endVideoCall({
              roomId: room.sid,
              epochTime: dayjs().unix(),
              createdAt: dayjs().toISOString(),
            })
          }
        }

        videoCallConfigsStore.sendDataTrackMessage({
          type: DataMessageTypes.HangUp,
          payload: null,
        })

        videoCallConfigsStore.removeParticipantTracks(room.localParticipant)
      }

      videoCallConfigsStore.disconnectFromRoom()
      videoCallConfigsStore.closeVideoConversation()
      EventBus.emit(VideoCallEvents.RedirectToConversation)
    },

    async inviteToVideoChat(dto: { data: IVideoCallInviteData }) {
      const coversationsStore = useConversationsStore()
      if (!coversationsStore.conversation.data || !room) {
        return
      }

      try {
        await apiTransport.post<InviteVideoCallDTO>(
          '/video_calls/invite_to_video_call',
          new InviteVideoCallDTO(dto),
          InviteVideoCallDTO
        )
      } catch (e) {
        const error = e as AxiosError
        ElMessage.error(`Error during inviting to video call: ${error.message}`)
      }
    },

    checkIsPatientVideoTestFailed(msg: IMessage) {
      const result = msg.attachments.find(
        ({ contentType }) =>
          contentType === AttachmentContentTypes.QuickTestResults
      )
      if (result) {
        const message = result.inline ? JSON.parse(result.inline) : null
        if (!message.allPassed) {
          this.isPatientVideoTestFailed = true
        }
      }
    },
  },
})
